import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppHeader from "@/components/AppHeader";
import { CardGridSkeleton, ErrorState, EmptyState } from "@/components/shared/StateViews";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ADMIN } from "@/constants/testIds/admin";
import { friendlyError } from "@/lib/errors";
import { Search, Users2, UserCheck, Sparkles, FileCheck2, ChevronLeft, ChevronRight, ShieldCheck, Smartphone, Download, Bell, Send, RotateCcw, Loader2, Plus, Pencil, KeyRound, LogOut, UserRoundCheck } from "lucide-react";
import { toast } from "sonner";

export const PERSONA_RESET_WORD = "RESETAR";
const PERSONA_BADGE = {
  none: { label: "Sem persona", cls: "bg-brand-elevated text-brand-gold" },
  draft: { label: "Persona em rascunho", cls: "bg-brand-elevated text-brand-gold" },
  ready: { label: "Persona pronta", cls: "bg-brand-elevated text-brand-ink2" },
  done: { label: "Onboarding concluído", cls: "bg-brand-sage/15 text-brand-sage" },
};

// Espelha effective_onboarding_status do backend: o status gravado só vale
// como "pronta" quando a persona foi confirmada de fato (conta legada pode
// ter brand_persona_pronta gravado sem confirmação).
export function personaBadgeKey(u) {
  if (u?.status_onboarding === "concluido") return "done";
  if (u?.persona_confirmada) return "ready";
  if (u?.has_persona) return "draft";
  return "none";
}

const STATUS_FILTERS = [
  { value: "all", label: "Todos" },
  { value: "pending", label: "Em análise" },
  { value: "allowed", label: "Liberados" },
  { value: "blocked", label: "Bloqueados" },
];
const PLAN_OPTIONS = [
  { value: "free", label: "Gratuito" },
  { value: "pro", label: "Pro" },
  { value: "premium", label: "Premium" },
];
const STATUS_BADGE = {
  allowed: { label: "Liberado", cls: "bg-brand-sage/15 text-brand-sage" },
  pending: { label: "Em análise", cls: "bg-brand-elevated text-brand-gold" },
  blocked: { label: "Bloqueado", cls: "bg-brand-rose/15 text-brand-rose" },
};

export default function Admin() {
  const { user: me } = useAuth();
  const [funnel, setFunnel] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null); // { user, next }
  const [resetTarget, setResetTarget] = useState(null); // user a resetar a Brand Persona
  const [createOpen, setCreateOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page };
      if (query) params.query = query;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, [page, query, status]);

  useEffect(() => {
    api.get("/admin/funnel").then((r) => setFunnel(r.data)).catch((err) => {
      setFunnel(null);
      toast.error(friendlyError(err));
    });
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Debounce the search field into the committed `query` that drives loadUsers.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Optimistic PATCH with rollback (Story 2.5 AC1).
  const patchUser = async (id, patch, prev) => {
    setUsers((list) => list.map((u) => (u.id === id ? { ...u, ...patch } : u)));
    try {
      await api.patch(`/admin/users/${id}`, patch);
      toast.success("Alteração salva ✦");
    } catch (err) {
      setUsers((list) => list.map((u) => (u.id === id ? { ...u, ...prev } : u)));
      toast.error(friendlyError(err));
    }
  };

  const requestAccessChange = (u, checked) => {
    // Switch on = liberar (allowed); off = bloquear (blocked). Confirm first.
    setConfirm({ user: u, next: checked ? "allowed" : "blocked" });
  };

  const applyAccessChange = () => {
    if (!confirm) return;
    const { user: u, next } = confirm;
    patchUser(u.id, { access_status: next }, { access_status: u.access_status });
    setConfirm(null);
  };

  const changePlan = (u, plan) => {
    if (plan === u.plan) return;
    patchUser(u.id, { plan }, { plan: u.plan });
  };

  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <div className="min-h-screen bg-brand-bg" data-testid={ADMIN.page}>
      <AppHeader />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10 pb-24 md:pb-10">
        <div className="mb-7">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-brand-rose font-ui mb-2">
            <ShieldCheck className="w-3.5 h-3.5" /> Painel admin
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-brand-ink">Controle de acesso</h1>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-brand-ink2 text-[14.5px] mt-1">Gerencie acessos e planos de mentoradas. Planos liberam recursos; não cobram nada.</p>
            <button type="button" data-testid={ADMIN.create} onClick={() => setCreateOpen(true)} className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-full bg-brand-rose hover:bg-brand-roseHover text-white text-[13px] font-ui">
              <Plus className="w-4 h-4" /> Criar conta
            </button>
          </div>
        </div>

        {/* Funnel */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-8">
          <FunnelCard icon={Users2} label="Signups (30d)" value={funnel?.signups_30d} testid={ADMIN.funnelSignups} />
          <FunnelCard icon={UserCheck} label="Onboarding concluído" value={funnel?.onboarding_concluido} testid={ADMIN.funnelOnboarding} />
          <FunnelCard icon={Sparkles} label="Avatares completos" value={funnel?.avatares_completos} testid={ADMIN.funnelAvatars} />
          <FunnelCard icon={FileCheck2} label="Artefatos salvos" value={funnel?.artefatos_salvos} testid={ADMIN.funnelArtifacts} />
        </div>

        <div className="text-[11px] tracking-[0.24em] uppercase text-brand-ink3 font-ui mb-3">App no celular</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-8">
          <FunnelCard icon={Smartphone} label="Instalaram (30d)" value={funnel?.pwa_installed_30d} testid={ADMIN.pwaInstalled} />
          <FunnelCard icon={Download} label="Aceitaram o prompt" value={funnel?.pwa_install_accepted_30d} testid={ADMIN.pwaAccepted} />
          <FunnelCard icon={Bell} label="Avisos ligados" value={funnel?.pwa_subs_active} testid={ADMIN.pwaSubs} />
          <FunnelCard icon={Send} label="Pushes 7d" value={funnel?.pwa_push_sent_7d} testid={ADMIN.pwaSent} />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ink3" />
            <input
              data-testid={ADMIN.search}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por nome ou e-mail…"
              className="w-full pl-10 pr-4 py-3 rounded-full bg-brand-surface border border-brand-line focus:border-brand-rose focus:outline-none text-[16px] text-white placeholder:text-brand-ink3 min-h-[44px]"
            />
          </div>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-soft -mx-1 px-1" data-testid={ADMIN.statusFilter}>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                data-testid={`admin-filter-${f.value}`}
                onClick={() => { setStatus(f.value); setPage(1); }}
                className={`shrink-0 px-4 py-2.5 rounded-full text-[12.5px] font-ui transition-colors min-h-[44px] ${
                  status === f.value
                    ? "bg-brand-rose text-white"
                    : "bg-brand-surface border border-brand-line text-brand-ink2 hover:text-brand-ink"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <CardGridSkeleton count={6} />
        ) : error ? (
          <ErrorState message={error} onRetry={loadUsers} testid="admin-error" />
        ) : users.length === 0 ? (
          <EmptyState icon={Users2} title="Nenhum usuário encontrado" description="Ajuste a busca ou o filtro de status." testid="admin-empty" />
        ) : (
          <>
            {/* Mobile cards */}
            <div className="space-y-3 md:hidden">
              {users.map((u) => (
                <UserCard key={u.id} u={u} isSelf={u.id === me?.id} onAccess={requestAccessChange} onPlan={changePlan} onReset={setResetTarget} onDetails={setDetailTarget} />
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden md:block bg-brand-surface border border-brand-line rounded-2xl overflow-hidden">
              <table className="w-full table-fixed text-left">
                <thead>
                  <tr className="border-b border-brand-line text-[11px] tracking-[0.16em] uppercase text-brand-ink3 font-ui">
                    <th className="px-5 py-3.5 font-normal">Usuário</th>
                    <th className="px-5 py-3.5 font-normal">Status</th>
                    <th className="px-5 py-3.5 font-normal">Plano</th>
                    <th className="px-5 py-3.5 font-normal">Persona</th>
                    <th className="px-5 py-3.5 font-normal">Último acesso</th>
                    <th className="px-5 py-3.5 font-normal text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <UserRow key={u.id} u={u} isSelf={u.id === me?.id} onAccess={requestAccessChange} onPlan={changePlan} onReset={setResetTarget} onDetails={setDetailTarget} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-6">
                <button
                  data-testid={ADMIN.prevPage}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="p-2.5 rounded-full bg-brand-surface border border-brand-line text-brand-ink2 hover:text-brand-rose disabled:opacity-40 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-[13px] text-brand-ink2 font-ui">Página {page} de {totalPages}</span>
                <button
                  data-testid={ADMIN.nextPage}
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="p-2.5 rounded-full bg-brand-surface border border-brand-line text-brand-ink2 hover:text-brand-rose disabled:opacity-40 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <AlertDialog open={Boolean(confirm)} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid={ADMIN.confirmDialog} className="bg-brand-surface border-brand-line">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-brand-ink">
              {confirm?.next === "allowed" ? "Liberar acesso?" : "Bloquear acesso?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-brand-ink2">
              {confirm?.next === "allowed"
                ? `${confirm?.user?.name || confirm?.user?.email} vai poder entrar na plataforma.`
                : `${confirm?.user?.name || confirm?.user?.email} perderá o acesso na próxima requisição.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-brand-elevated border-brand-line text-brand-ink2">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid={ADMIN.confirmAction}
              onClick={applyAccessChange}
              className="bg-brand-rose hover:bg-brand-roseHover text-white"
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PersonaResetDialog
        user={resetTarget}
        onClose={() => setResetTarget(null)}
        onDone={loadUsers}
      />
      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} onDone={loadUsers} />
      <UserDetailDialog target={detailTarget} isSelf={detailTarget?.id === me?.id} onClose={() => setDetailTarget(null)} onDone={loadUsers} />
    </div>
  );
}

const EMPTY_ACCOUNT = { name: "", email: "", password: "", plan: "free", access_status: "allowed" };
const passwordBytes = (value) => new TextEncoder().encode(value || "").length;

function Field({ testid, label, type = "text", value, onChange, required = false }) {
  return <label className="block text-[12px] text-brand-ink2 font-ui">{label}
    <input data-testid={testid} type={type} value={value} required={required} maxLength={type === "password" ? 72 : undefined} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full min-h-[42px] rounded-lg bg-brand-bg border border-brand-line px-3 text-white focus:outline-none focus:border-brand-rose" />
  </label>;
}

function AccountSelect({ testid, label, value, onChange, options }) {
  return <label className="block text-[12px] text-brand-ink2 font-ui">{label}
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full min-h-[42px] rounded-lg bg-brand-bg border border-brand-line px-3 text-white">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>;
}

export function CreateUserDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState(EMPTY_ACCOUNT);
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  useEffect(() => { if (open) { setForm(EMPTY_ACCOUNT); setBusy(false); } }, [open]);
  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (passwordBytes(form.password) > 72) return toast.error("A senha pode ter no máximo 72 bytes. Use menos caracteres ou evite emojis.");
    setBusy(true);
    try {
      await api.post("/admin/users", { ...form, name: form.name.trim(), email: form.email.trim().toLowerCase() });
      toast.success("Conta criada sem cobrança nem e-mail ✦");
      onClose(); await onDone?.();
    } catch (err) { toast.error(friendlyError(err)); } finally { setBusy(false); }
  };
  return <AlertDialog open={open} onOpenChange={(value) => !value && !busy && onClose()}>
      <AlertDialogContent data-testid={ADMIN.createDialog} className="bg-brand-surface border-brand-line max-h-[90vh] overflow-y-auto overflow-x-hidden">
      <AlertDialogHeader><AlertDialogTitle className="font-display text-2xl text-brand-ink">Criar conta manual</AlertDialogTitle><AlertDialogDescription className="text-brand-ink2">A conta não gera cobrança nem envia e-mail. Defina a senha inicial para a mentorada.</AlertDialogDescription></AlertDialogHeader>
      <form onSubmit={submit} className="space-y-3"><Field testid={ADMIN.createName} label="Nome" value={form.name} onChange={set("name")} required /><Field testid={ADMIN.createEmail} label="E-mail" type="email" value={form.email} onChange={set("email")} required /><Field testid={ADMIN.createPassword} label="Senha inicial (mínimo 6 caracteres)" type="password" value={form.password} onChange={set("password")} required />
        <div className="grid grid-cols-2 gap-3"><AccountSelect testid={ADMIN.createPlan} label="Plano de recursos" value={form.plan} onChange={set("plan")} options={PLAN_OPTIONS} /><AccountSelect testid={ADMIN.createAccess} label="Acesso" value={form.access_status} onChange={set("access_status")} options={STATUS_FILTERS.slice(1)} /></div>
        <AlertDialogFooter><AlertDialogCancel type="button" disabled={busy} className="bg-brand-elevated border-brand-line text-brand-ink2">Cancelar</AlertDialogCancel><button data-testid={ADMIN.createSubmit} disabled={busy || form.password.length < 6} className="min-h-[40px] px-4 rounded-md bg-brand-rose text-white disabled:opacity-50">{busy && <Loader2 className="inline w-4 h-4 animate-spin mr-2" />}Criar conta</button></AlertDialogFooter>
      </form>
    </AlertDialogContent>
  </AlertDialog>;
}

export function UserDetailDialog({ target, isSelf, onClose, onDone }) {
  const { startImpersonation } = useAuth();
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null);
  useEffect(() => {
    if (!target) return;
    setDetail(null); setForm(null); setAction(null);
    api.get(`/admin/users/${target.id}`).then(({ data }) => {
      const u = data.user || data;
      setDetail({ ...u, history: data.history || [] });
      setForm({ name: u.name || "", email: u.email || "", plan: u.plan || "free", access_status: u.access_status || "pending" });
    }).catch((err) => { toast.error(friendlyError(err)); onClose(); });
  // `target.id` is the identity boundary; parent callbacks need not refetch it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id]);
  if (!target) return null;
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  const save = async () => {
    if (!form || busy) return;
    const patch = Object.fromEntries(Object.entries(form).filter(([key, value]) => value !== detail[key]));
    if (!Object.keys(patch).length) return toast.error("Nada para alterar.");
    setBusy(true); try { const { data } = await api.patch(`/admin/users/${target.id}`, patch); setDetail((old) => ({ ...old, ...(data.user || data) })); toast.success("Dados da conta salvos ✦"); await onDone?.(); } catch (err) { toast.error(friendlyError(err)); } finally { setBusy(false); }
  };
  const doAction = async () => {
    if (!action || busy) return;
    if (action.kind === "password" && passwordBytes(action.password) > 72) return toast.error("A senha pode ter no máximo 72 bytes. Use menos caracteres ou evite emojis.");
    setBusy(true);
    try {
      if (action.kind === "password") await api.post(`/admin/users/${target.id}/password`, { password: action.password });
      if (action.kind === "revoke") await api.post(`/admin/users/${target.id}/revoke-sessions`);
      if (action.kind === "impersonate") { await startImpersonation(target.id); onClose(); return; }
      toast.success(action.kind === "password" ? "Nova senha definida e sessões encerradas ✦" : "Sessões encerradas ✦"); setAction(null);
      const { data } = await api.get(`/admin/users/${target.id}`); setDetail({ ...(data.user || data), history: data.history || [] });
    } catch (err) { toast.error(friendlyError(err)); } finally { setBusy(false); }
  };
  const actionTitle = action?.kind === "password" ? "Definir nova senha?" : action?.kind === "revoke" ? "Encerrar todas as sessões?" : "Entrar temporariamente nesta conta?";
  return <><AlertDialog open={Boolean(target)} onOpenChange={(value) => !value && !busy && onClose()}>
    <AlertDialogContent data-testid={ADMIN.detailDialog} className="bg-brand-surface border-brand-line max-h-[90vh] overflow-y-auto overflow-x-hidden max-w-xl">
      <AlertDialogHeader><AlertDialogTitle className="font-display text-2xl text-brand-ink">Conta de {detail?.name || target.name || target.email}</AlertDialogTitle><AlertDialogDescription className="text-brand-ink2">Plano libera recursos e não realiza cobrança.</AlertDialogDescription></AlertDialogHeader>
      {!form ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : <div className="space-y-4"><div className="grid sm:grid-cols-2 gap-3"><Field testid={ADMIN.detailName} label="Nome" value={form.name} onChange={set("name")} /><Field testid={ADMIN.detailEmail} label="E-mail" type="email" value={form.email} onChange={set("email")} /><AccountSelect testid={ADMIN.detailPlan} label="Plano de recursos" value={form.plan} onChange={set("plan")} options={PLAN_OPTIONS} /><AccountSelect testid={ADMIN.detailAccess} label="Acesso" value={form.access_status} onChange={set("access_status")} options={STATUS_FILTERS.slice(1)} /></div>
        <p className="text-[11px] text-brand-ink3">Trocar o e-mail ou bloquear o acesso encerra as sessões abertas desta usuária.</p><button type="button" data-testid={ADMIN.detailSave} onClick={save} disabled={busy} className="w-full min-h-[42px] rounded-full bg-brand-rose text-white disabled:opacity-50">Salvar alterações</button>
        {!isSelf && <div className="grid sm:grid-cols-3 gap-2 pt-2 border-t border-brand-line"><button type="button" data-testid={ADMIN.password} onClick={() => setAction({ kind: "password", password: "" })} className="min-h-[42px] rounded-lg border border-brand-line text-brand-ink2 hover:text-brand-rose inline-flex items-center justify-center gap-2 text-[12px]"><KeyRound className="w-4 h-4" /> Nova senha</button><button type="button" data-testid={ADMIN.revokeSessions} onClick={() => setAction({ kind: "revoke" })} className="min-h-[42px] rounded-lg border border-brand-line text-brand-ink2 hover:text-brand-rose inline-flex items-center justify-center gap-2 text-[12px]"><LogOut className="w-4 h-4" /> Encerrar sessões</button><button type="button" data-testid={ADMIN.impersonate} disabled={detail?.access_status !== "allowed" || detail?.is_admin || detail?.roles?.includes("admin")} onClick={() => setAction({ kind: "impersonate" })} className="min-h-[42px] rounded-lg border border-brand-line text-brand-ink2 hover:text-brand-rose inline-flex items-center justify-center gap-2 text-[12px] disabled:opacity-40"><UserRoundCheck className="w-4 h-4" /> Atender conta</button></div>}
        {isSelf && <p className="text-[12px] text-brand-ink3">Para sua senha, sessões e persona, use Minha conta.</p>}
        <div><h3 className="text-[11px] uppercase tracking-widest text-brand-ink3 mb-2">Histórico administrativo</h3><div data-testid={ADMIN.detailHistory} className="space-y-2 max-h-40 overflow-y-auto overflow-x-hidden">{detail.history?.length ? detail.history.map((event, index) => <HistoryItem key={event.id || index} event={event} />) : <p className="text-[12px] text-brand-ink3">Nenhuma operação administrativa registrada.</p>}</div></div>
      </div>}<AlertDialogFooter><AlertDialogCancel data-testid={ADMIN.detailClose} disabled={busy} className="bg-brand-elevated border-brand-line text-brand-ink2">Fechar</AlertDialogCancel></AlertDialogFooter>
    </AlertDialogContent></AlertDialog>
    <AlertDialog open={Boolean(action)} onOpenChange={(value) => !value && !busy && setAction(null)}><AlertDialogContent data-testid={action?.kind === "password" ? ADMIN.passwordDialog : action?.kind === "revoke" ? ADMIN.revokeDialog : ADMIN.impersonateDialog} className="bg-brand-surface border-brand-line"><AlertDialogHeader><AlertDialogTitle className="font-display text-2xl text-brand-ink">{actionTitle}</AlertDialogTitle><AlertDialogDescription className="text-brand-ink2">{action?.kind === "impersonate" ? "Você verá a plataforma como esta usuária por até 15 minutos. O acesso de admin fica preservado e você pode voltar a qualquer momento. Durante o atendimento, a conversa por voz e as alterações de senha ficam indisponíveis. Para redefinir a senha, volte ao admin." : "Esta ação não envia e-mail e é registrada no histórico."}</AlertDialogDescription></AlertDialogHeader>{action?.kind === "password" && <Field testid={ADMIN.passwordInput} label="Nova senha (mínimo 6 caracteres)" type="password" value={action.password} onChange={(password) => setAction({ kind: "password", password })} />}<AlertDialogFooter><AlertDialogCancel data-testid={ADMIN.actionCancel} disabled={busy} className="bg-brand-elevated border-brand-line text-brand-ink2">Cancelar</AlertDialogCancel><AlertDialogAction data-testid={action?.kind === "password" ? ADMIN.passwordConfirm : action?.kind === "revoke" ? ADMIN.revokeConfirm : ADMIN.impersonateConfirm} disabled={busy || (action?.kind === "password" && action.password.length < 6)} onClick={(e) => { e.preventDefault(); doAction(); }} className="bg-brand-rose text-white">Confirmar</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
  </>;
}

const HISTORY_LABELS = { admin_user_created: "Conta criada", admin_user_updated: "Dados alterados", admin_user_password_set: "Senha redefinida", admin_user_sessions_revoked: "Sessões encerradas", admin_impersonation_started: "Atendimento temporário iniciado", admin_impersonated_write_attempted: "Ação durante atendimento" };
const HISTORY_FIELDS = { name: "Nome", email: "E-mail", plan: "Plano", access_status: "Acesso" };
const HISTORY_VALUES = { free: "Gratuito", pro: "Pro", premium: "Premium", allowed: "Liberado", blocked: "Bloqueado", pending: "Em análise" };
const safeHistoryField = (key) => HISTORY_FIELDS[key] && !/password|token|hash/i.test(key);
const historyValue = (value) => HISTORY_VALUES[value] || String(value ?? "—");
export function formatHistoryChanges(properties = {}) {
  const before = properties.before || {};
  const after = properties.after || {};
  const fields = Array.isArray(properties.fields) ? properties.fields : Object.keys(properties.fields || after);
  return fields.filter(safeHistoryField).map((key) => {
    const next = after[key] ?? (properties.fields && !Array.isArray(properties.fields) ? properties.fields[key] : undefined);
    const previous = before[key];
    const shown = next === undefined ? historyValue(previous) : previous === undefined ? historyValue(next) : `${historyValue(previous)} → ${historyValue(next)}`;
    return `${HISTORY_FIELDS[key]}: ${shown}`;
  }).join(" · ");
}
export function HistoryItem({ event }) {
  const props = event.properties || {};
  const summary = formatHistoryChanges(props);
  const actor = event.actor?.name || event.actor?.email;
  return <div className="text-[12px] p-2 rounded bg-brand-bg text-brand-ink2"><strong>{HISTORY_LABELS[event.event_type || event.type] || "Operação administrativa"}</strong>{actor ? ` por ${actor}` : ""}{event.actor?.name && event.actor?.email ? ` (${event.actor.email})` : ""}{summary ? ` — ${summary}` : ""}<div className="text-brand-ink3 mt-0.5">{event.created_at ? new Date(event.created_at).toLocaleString("pt-BR") : ""}</div></div>;
}

// Duplo aceite: 1) o que será apagado e o que fica; 2) digitar RESETAR.
export function PersonaResetDialog({ user, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const typed = word.trim().toUpperCase();
  const canConfirm = typed === PERSONA_RESET_WORD && !busy;
  const who = user?.name || user?.email || "";

  useEffect(() => { setStep(1); setWord(""); setBusy(false); }, [user?.id]);

  const run = async () => {
    if (!user || !canConfirm) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/persona/reset`, { confirm: typed });
      toast.success(`Brand Persona de ${who} apagada. Onboarding reaberto ✦`);
      onClose?.();
      await onDone?.();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={Boolean(user)} onOpenChange={(o) => { if (!o && !busy) onClose?.(); }}>
      <AlertDialogContent data-testid={ADMIN.personaResetDialog} className="bg-brand-surface border-brand-line">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl text-brand-ink">
            {step === 1 ? `Resetar a Brand Persona de ${who}?` : "Confirme digitando RESETAR"}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            {step === 1 ? (
              <div className="text-brand-ink2 text-[13.5px] font-ui space-y-3">
                <p>A usuária volta para a entrevista inicial, como no primeiro acesso, e fica sem painel e agentes até confirmar a nova persona e o avatar.</p>
                <div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-brand-rose mb-1">Será apagado</div>
                  <ul className="list-disc pl-5 space-y-0.5"><li>A Brand Persona atual (todos os campos).</li></ul>
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-brand-sage mb-1">Fica guardado</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    <li>Avatares, conteúdos, calendário e conversas (a entrevista anterior é marcada como anterior).</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-brand-ink2 text-[13.5px] font-ui">
                  Esta ação não pode ser desfeita. Digite <span className="text-brand-ink font-semibold">{PERSONA_RESET_WORD}</span> para confirmar o reset de {who}.
                </p>
                <input
                  data-testid={ADMIN.personaResetInput}
                  value={word}
                  onChange={(e) => setWord(e.target.value)}
                  autoFocus
                  autoComplete="off"
                  placeholder={PERSONA_RESET_WORD}
                  onKeyDown={(e) => { if (e.key === "Enter") run(); }}
                  className="w-full px-4 py-3 rounded-xl border border-brand-line bg-brand-elevated text-white focus:border-brand-rose focus:outline-none text-[16px] tracking-[0.2em] uppercase"
                />
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <button
            type="button"
            data-testid={ADMIN.personaResetCancel}
            onClick={() => onClose?.()}
            disabled={busy}
            className="min-h-[40px] px-4 rounded-md bg-brand-elevated border border-brand-line text-brand-ink2 hover:text-brand-rose text-[13.5px] font-ui transition-colors"
          >
            Cancelar
          </button>
          {step === 1 ? (
            <button
              type="button"
              data-testid={ADMIN.personaResetContinue}
              onClick={() => setStep(2)}
              className="min-h-[40px] px-4 rounded-md bg-brand-rose hover:bg-brand-roseHover text-white text-[13.5px] font-ui transition-colors"
            >
              Entendi, continuar
            </button>
          ) : (
            <button
              type="button"
              data-testid={ADMIN.personaResetConfirm}
              onClick={run}
              disabled={!canConfirm}
              className="min-h-[40px] px-4 rounded-md bg-brand-rose hover:bg-brand-roseHover text-white text-[13.5px] font-ui transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Apagar e reabrir onboarding
            </button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PersonaBadge({ u }) {
  const b = PERSONA_BADGE[personaBadgeKey(u)];
  return (
    <span
      data-testid={`${ADMIN.personaBadge}-${u.id}`}
      className={`px-2.5 py-1 rounded-full text-[10.5px] tracking-[0.12em] uppercase font-ui whitespace-nowrap ${b.cls}`}
      title={`status gravado: ${u.status_onboarding || "pendente"}`}
    >
      {b.label}
    </span>
  );
}

function PersonaResetButton({ u, isSelf, onReset }) {
  return (
    <button
      type="button"
      data-testid={`${ADMIN.personaReset}-${u.id}`}
      onClick={() => onReset(u)}
      disabled={isSelf}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-elevated border border-brand-line text-[12px] text-brand-ink2 hover:text-brand-rose hover:border-brand-rose transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
      title={isSelf ? "Para a sua própria Brand Persona, use Minha conta" : "Apagar a Brand Persona e reabrir o onboarding"}
    >
      <RotateCcw className="w-3.5 h-3.5" /> Resetar persona
    </button>
  );
}

function FunnelCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-brand-surface border border-brand-line rounded-2xl p-4 lg:p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-brand-rose mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-[10.5px] tracking-[0.14em] uppercase text-brand-ink2 font-ui">{label}</span>
      </div>
      <div className="font-display text-3xl lg:text-4xl text-brand-ink">
        {value == null ? "—" : value}
      </div>
    </div>
  );
}

function AccessSwitch({ u, isSelf, onAccess }) {
  return (
    <Switch
      data-testid={`${ADMIN.accessSwitch}-${u.id}`}
      checked={u.access_status === "allowed"}
      disabled={isSelf}
      onCheckedChange={(checked) => onAccess(u, checked)}
      aria-label={u.access_status === "allowed" ? "Bloquear acesso" : "Liberar acesso"}
    />
  );
}

function PlanSelect({ u, onPlan }) {
  return (
    <Select value={u.plan} onValueChange={(v) => onPlan(u, v)}>
      <SelectTrigger data-testid={`${ADMIN.planSelect}-${u.id}`} className="w-[130px] rounded-full border-brand-line bg-brand-bg text-[12.5px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PLAN_OPTIONS.map((p) => (
          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function StatusBadge({ status }) {
  const b = STATUS_BADGE[status] || STATUS_BADGE.pending;
  return (
    <span className={`px-2.5 py-1 rounded-full text-[10.5px] tracking-[0.12em] uppercase font-ui ${b.cls}`}>
      {b.label}
    </span>
  );
}

function lastSeen(u) {
  const d = u.last_seen || u.created_at;
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch { return "—"; }
}

function UserRow({ u, isSelf, onAccess, onPlan, onReset, onDetails }) {
  return (
    <tr className="border-b border-brand-line last:border-0" data-testid={`${ADMIN.userRow}-${u.id}`}>
      <td className="px-5 py-4">
        <div className="font-ui text-[14px] text-brand-ink truncate" title={u.name || "—"}>{u.name || "—"}</div>
        <div className="max-w-[220px] text-[12.5px] text-brand-ink2 truncate" title={u.email}>{u.email}</div>
      </td>
      <td className="px-5 py-4"><StatusBadge status={u.access_status} /></td>
      <td className="px-5 py-4"><PlanSelect u={u} onPlan={onPlan} /></td>
      <td className="px-5 py-4">
        <div className="flex flex-col items-start gap-2">
          <PersonaBadge u={u} />
          <PersonaResetButton u={u} isSelf={isSelf} onReset={onReset} />
        </div>
      </td>
      <td className="px-5 py-4 text-[12.5px] text-brand-ink2">{lastSeen(u)}</td>
      <td className="px-5 py-4">
        <div className="flex justify-end gap-3 items-center">
          <AccessSwitch u={u} isSelf={isSelf} onAccess={onAccess} />
          <button type="button" data-testid={`${ADMIN.details}-${u.id}`} onClick={() => onDetails(u)} className="p-2 rounded-full text-brand-ink2 hover:text-brand-rose" aria-label={`Abrir conta de ${u.name || u.email}`}><Pencil className="w-4 h-4" /></button>
        </div>
      </td>
    </tr>
  );
}

function UserCard({ u, isSelf, onAccess, onPlan, onReset, onDetails }) {
  return (
    <div className="bg-brand-surface border border-brand-line rounded-2xl p-4" data-testid={`${ADMIN.userRow}-${u.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-ui text-[15px] text-brand-ink truncate">{u.name || "—"}</div>
          <div className="text-[12.5px] text-brand-ink2 truncate">{u.email}</div>
        </div>
        <StatusBadge status={u.access_status} />
      </div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <PersonaBadge u={u} />
        <PersonaResetButton u={u} isSelf={isSelf} onReset={onReset} />
      </div>
      <div className="flex items-center justify-between gap-3">
        <PlanSelect u={u} onPlan={onPlan} />
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-brand-ink2 font-ui">Liberado</span>
          <AccessSwitch u={u} isSelf={isSelf} onAccess={onAccess} />
        </div>
      </div>
      <button type="button" data-testid={`${ADMIN.details}-${u.id}`} onClick={() => onDetails(u)} className="mt-3 w-full min-h-[40px] rounded-full border border-brand-line text-[12px] text-brand-ink2 hover:text-brand-rose inline-flex items-center justify-center gap-2"><Pencil className="w-3.5 h-3.5" /> Ver e editar conta</button>
      {isSelf && (
        <div className="text-[11px] text-brand-ink3 font-ui mt-2">Você não pode alterar o próprio acesso.</div>
      )}
    </div>
  );
}
